/**
 * Browser App Commands
 * 
 * NOTE: These commands are also registered in aiCommands.ts for AI tool use.
 * - browserwindow_create: Creates and opens browser windows
 * - browser_navigate: Navigates browser tabs to new URLs
 * 
 * The commands here are registered via commandRegistryStore for manual execution.
 * The AI versions in aiCommands.ts include formatViewData and are rendered via
 * canonical tool renderers (BrowserWindowCreateRenderer, BrowserNavigateRenderer).
 */

import type { Command, SelfContainedCommand, CommandExecutionContext } from '../../../types/commands';
import { createSuccessResult, createErrorResult, StandardToolResult } from '../../../types/standardToolResults';
import { createFileLogger } from '../../../utils/logger';
import { entityService } from '../../../services/entityService';
import { generateUniqueLLMId } from '../../../utils/entityIdGenerator';
import useSpaceStore from '../../../stores/spaceStore';
import { browserNavigateCommand } from './navigate';
import { browserGuideActionCommand } from './browserGuideAction';
import { browserObservePageCommand } from './browserObservePage';

const log = createFileLogger('BROWSER_COMMANDS');

export const browserCreateWindowCommand: SelfContainedCommand = {
  id: 'browserwindow_create',
  description: 'Create a BrowserWindow entity (optional) and open a Browser app window. If persist=false, just open a window without saving.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',
  schema: {
    parameters: [
      { name: 'spaceId', type: 'string', description: 'Space to open in', required: false },
      { name: 'initialUrl', type: 'string', description: 'URL to load initially', required: false },
      { name: 'persist', type: 'boolean', description: 'Persist a BrowserWindow entity', required: false, defaultValue: false },
      { name: 'homeUrl', type: 'string', description: 'Home URL setting when persisting', required: false },
    ]
  },
  examples: { good: [], bad: [] },
  performance: { expectedDuration: 300, complexity: 'low' },
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    try {
      const { spaceId: providedSpaceId, initialUrl, persist = false, homeUrl } = params;
      const spaceId = providedSpaceId || context.spaceId;
      const { actions } = useSpaceStore.getState();

      if (!persist) {
        // Create temporary browser window without entity
        const windowId = await actions.createWindow({
          appType: 'browser',
          position: { x: 100, y: 100 },
          size: { width: 1000, height: 700 },
          applicationState: initialUrl ? { props: { initialUrl } } : undefined
        });
        
        actions.updateWindow(windowId, { title: 'Browser' });
        actions.setActiveWindow(windowId);
        
        return createSuccessResult('Opened browser window', { windowId });
      }

      // Persisted flow: create BrowserWindow (and optional BrowserPage)
      const llmId = await generateUniqueLLMId('BrowserWindow', async (id: string) => {
        try { await entityService.get(id); return true; } catch { return false; }
      });
      const windowEntity = await entityService.create({
        llmId,
        name: 'Browser Window',
        entityType: 'BrowserWindow',
        skeleton: {
          '@type': 'BrowserWindow',
          tabs: [],
          settings: { homeUrl: homeUrl || 'https://www.google.com' }
        }
      });

      let pageEntity: any | null = null;
      let tabRef: any | null = null;
      {
        const urlToUse = initialUrl || (windowEntity?.skeleton?.settings?.homeUrl) || 'https://www.google.com';
        const pageLlmId = await generateUniqueLLMId('BrowserPage', async (id: string) => {
          try { await entityService.get(id); return true; } catch { return false; }
        });
        pageEntity = await entityService.create({
          llmId: pageLlmId,
          name: urlToUse,
          entityType: 'BrowserPage',
          skeleton: {
            '@type': 'BrowserPage',
            url: urlToUse,
            history: { entries: [urlToUse], index: 0 },
            lastVisitedAt: new Date().toISOString()
          }
        });
        tabRef = {
          id: `tab_${Math.random().toString(36).slice(2, 10)}`,
          pageId: pageEntity._id || pageEntity.id,
          createdAt: new Date().toISOString(),
        };
        await entityService.update(windowEntity._id || windowEntity.id, {
          skeleton: {
            ...(windowEntity.skeleton || {}),
            tabs: [tabRef],
            activeTabId: tabRef.id,
          }
        });
      }

      // Fetch fresh window entity so the window has tabs on first render
      const freshWindowEntity = await entityService.get(windowEntity._id || windowEntity.id);
      
      // Use space_open_window command for proper multi-store coordination
      const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
      const { executeCommand } = useCommandRegistryStore.getState();
      
      const result = (await executeCommand('space_open_window', {
        entityId: freshWindowEntity._id || freshWindowEntity.id
      })) as import('../../../types/standardToolResults').StandardToolResult;

      if (result.status !== 'success') {
        return createErrorResult('Failed to open browser window', result.message || 'Unknown error');
      }

      return createSuccessResult('Created and opened BrowserWindow', { 
        windowEntity, 
        pageEntity, 
        tabRef, 
        windowId: result.data?.windowId 
      });
    } catch (error: any) {
      log.error('[browserwindow_create] Error:', error);
      return createErrorResult('Failed to create/open browser window', error?.message || 'Unknown error');
    }
  }
};

export const browserCommands: (SelfContainedCommand | Command)[] = [
  browserCreateWindowCommand,
  browserNavigateCommand,
  browserGuideActionCommand,
  browserObservePageCommand,
];

export default browserCommands;


